import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Check, Lock, Clock, ArrowRight } from "lucide-react";
import { Layout } from "../components/Layout";
import { fetchPreviewProject } from "../lib/api";
import { statusLabel, TIMELINE_STEPS, NEXT_STEP_HINT } from "../data/previewStatus";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};

export default function PreviewProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchPreviewProject(id).then(setData).catch(() => setError(true));
    window.scrollTo(0, 0);
  }, [id]);

  if (error) {
    return (
      <Layout>
        <section className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-display text-2xl font-bold text-[#0F0F0F]">Project not found</h1>
          <button onClick={() => navigate("/profile")} className="mt-6 text-sm font-semibold text-[#687280] hover:text-[#0F0F0F]">
            Back to profile
          </button>
        </section>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-24 animate-pulse space-y-4">
          <div className="h-8 bg-gray-100 rounded w-1/2" />
          <div className="h-40 bg-gray-100 rounded-2xl" />
        </div>
      </Layout>
    );
  }

  const { project, request } = data;
  const pct = project.progress_percentage || 0;
  const previewReady = !!project.preview_url;
  const currentIdx = TIMELINE_STEPS.indexOf(project.project_status);

  return (
    <Layout>
      <section className="max-w-3xl mx-auto px-6 lg:px-8 py-12" data-testid="preview-project-detail">
        <button onClick={() => navigate("/profile")} className="inline-flex items-center gap-1.5 text-sm text-[#687280] hover:text-[#0F0F0F] mb-6">
          <ArrowLeft size={16} /> My projects
        </button>

        <div className="bg-[#0F0F0F] rounded-3xl p-8 text-white">
          <div className="flex items-start justify-between gap-4">
            <div>
              <span className="inline-flex items-center rounded-full bg-[#D4AF37]/20 text-[#D4AF37] px-3 py-1 text-xs font-semibold">
                {statusLabel(project.project_status)}
              </span>
              <h1 className="font-display text-3xl font-extrabold mt-3">{project.project_name}</h1>
              <p className="text-[#A1A1AA] text-sm mt-1">
                Order {project.order_number} · Submitted {fmtDate(project.created_at)}
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[#A1A1AA] text-xs">Amount paid</p>
              <p className="font-display text-2xl font-bold">${project.amount}</p>
              <p className="text-green-400 text-xs mt-1">Paid</p>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between text-xs text-[#A1A1AA] mb-1.5">
              <span>Overall progress</span>
              <span className="font-semibold text-white">{pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-[#D4AF37] rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>

          <div className="mt-5 flex items-center gap-2 text-sm text-[#A1A1AA]">
            <Clock size={15} className="text-[#D4AF37]" />
            {NEXT_STEP_HINT[project.project_status] || "We'll keep you posted on progress."}
          </div>

          <button
            disabled={!previewReady}
            onClick={() => previewReady && window.open(project.preview_url, "_blank", "noopener,noreferrer")}
            className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors bg-[#D4AF37] text-[#0F0F0F] enabled:hover:bg-[#c5a030] disabled:bg-white/10 disabled:text-[#A1A1AA] disabled:cursor-not-allowed"
            data-testid="detail-live-preview-btn"
          >
            {previewReady ? <>Open Live Preview <ExternalLink size={15} /></> : <><Lock size={14} /> Preview not available yet</>}
          </button>
        </div>

        {/* Timeline */}
        <div className="mt-8 bg-white rounded-2xl border border-gray-100 p-7">
          <h2 className="font-display text-lg font-bold text-[#0F0F0F] mb-5">Project timeline</h2>
          <ol className="space-y-4">
            {TIMELINE_STEPS.map((s, i) => {
              const done = currentIdx >= 0 && i < currentIdx;
              const active = i === currentIdx;
              return (
                <li key={s} className="flex items-center gap-3">
                  <span
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                      done ? "bg-[#D4AF37] text-[#0F0F0F]" : active ? "bg-[#0F0F0F] text-[#D4AF37] ring-2 ring-[#D4AF37]" : "bg-[#F0F0F0] text-[#9CA3AF]"
                    }`}
                  >
                    {done ? <Check size={14} /> : i + 1}
                  </span>
                  <span className={`text-sm ${active ? "font-bold text-[#0F0F0F]" : done ? "text-[#0F0F0F]" : "text-[#9CA3AF]"}`}>
                    {statusLabel(s)}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Request details */}
        {request && (
          <div className="mt-8 bg-white rounded-2xl border border-gray-100 p-7">
            <h2 className="font-display text-lg font-bold text-[#0F0F0F] mb-5">Your request</h2>
            <div className="space-y-4 text-sm">
              <Detail label="Idea" value={request.idea_description} />
              {request.problem_statement && <Detail label="Problem" value={request.problem_statement} />}
              {request.target_users && <Detail label="Target users" value={request.target_users} />}
              {request.requested_features && <Detail label="Requested features" value={request.requested_features} />}
              {(request.design_style || request.preferred_colors) && (
                <Detail label="Design" value={[request.design_style, request.preferred_colors].filter(Boolean).join(" · ")} />
              )}
            </div>
          </div>
        )}

        <div className="mt-8 rounded-2xl bg-[#0F0F0F] p-7 text-center text-white" data-testid="cb-cta-card">
          <p className="font-display text-xl font-bold">Ready to build your app?</p>
          <p className="text-sm text-[#A1A1AA] mt-1 max-w-md mx-auto">
            Once your preview is approved, continue with a guided monthly build program — your app is released phase by phase with prompts, docs, files and checklists.
          </p>
          <button
            onClick={() => navigate("/continue-building")}
            className="mt-5 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-7 py-3 text-sm font-semibold hover:bg-[#c5a030] transition-colors"
            data-testid="cb-cta-btn"
          >
            Explore Continue Building <ArrowRight size={16} />
          </button>
        </div>
      </section>
    </Layout>
  );
}

const Detail = ({ label, value }) => (
  <div className="flex gap-4">
    <span className="text-[#9CA3AF] w-32 shrink-0">{label}</span>
    <span className="text-[#0F0F0F] flex-1 whitespace-pre-wrap">{value}</span>
  </div>
);
